<template>
  <a class="v-navbar-item" :class="{ 'v-navbar-item--active': isActive }" @click="onClick" @mousedown.middle="onMousedownMiddle">
    <slot />
  </a>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

interface Props {
  active?: boolean
  route?: string | { path?: string; name?: string }
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  route: undefined,
})

const emit = defineEmits<{
  click: [value: MouseEvent]
}>()

defineSlots<{
  default?(): any
}>()

const router = useRouter()
const currentRoute = useRoute()

const isActive = computed(() => {
  if (props.active) {
    return true
  }

  if (!props.route || !router || !currentRoute) {
    return false
  }

  if (typeof props.route === 'string') {
    return currentRoute.path === props.route
  }

  return props.route.path === currentRoute.path || props.route.name === currentRoute.name
})

async function onClick(event: MouseEvent) {
  if (props.route && router) {
    await router.push(props.route)
  }

  emit('click', event)
}

function onMousedownMiddle() {
  if (props.route && router) {
    const route = router.resolve(props.route)
    window.open(route.href, '_blank')
  }
}

defineExpose({
  isActive,
  onMousedownMiddle,
})
</script>

<style>
.v-navbar-item {
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
  display: flex;
  cursor: pointer;
  font-size: 16px;
  color: #000;
  padding: 8px;
  margin: 0 0.75vw;
  transition: color var(--transition);
  user-select: none;

  &:hover {
    text-decoration: unset;
    color: var(--color-primary);
  }
}

.v-navbar-end .v-navbar-item {
  margin: 0 0.4vw;
  padding-left: 4px;
  padding-right: 4px;
}

.v-navbar-item--active {
  border-bottom: 3px solid var(--color-primary);
  margin-bottom: -1.5px;
  padding-bottom: 6.5px;
}

.v-navbar-start .v-navbar-item {
  font-weight: 600;

  &:first-child {
    margin-left: 0;
  }
}

.v-navbar-end .v-navbar-item {
  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
}
</style>
